import { useMemo, useState } from "react";
import { ListChecks } from "lucide-react";
import { getSubtaskDateError } from "@/lib/task-date-validation";
import { SubtaskComposerRow } from "./SubtaskComposerRow";
import { DraftSubtaskRow, type DraftSubtask } from "./DraftSubtaskRow";
import type { SubtaskMemberOption } from "./SubtaskConfigFields";

export type { DraftSubtask } from "./DraftSubtaskRow";

interface TaskDraftSubtaskSectionProps {
  drafts: DraftSubtask[];
  onChange: (drafts: DraftSubtask[]) => void;
  memberId: string;
  isAdmin: boolean;
  members: SubtaskMemberOption[];
  minDate?: string;
  /** Data da tarefa-pai (`YYYY-MM-DD`) para validar limite das subtarefas. */
  parentDueDate?: string | null;
}

/**
 * Lista de subtarefas em rascunho durante a criação da tarefa-raiz.
 * Mantém estado local enquanto a tarefa ainda não existe no banco; o pai
 * persiste os rascunhos junto da criação.
 */
export function TaskDraftSubtaskSection({
  drafts,
  onChange,
  memberId,
  isAdmin,
  members,
  minDate,
  parentDueDate,
}: TaskDraftSubtaskSectionProps) {
  const [newTitle, setNewTitle] = useState("");
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  const memberName = (id: string) =>
    members.find((m) => m.id === id)?.display_name || "(sem nome)";

  /** Erro de data por rascunho: limite do pai + ordem cronológica entre vizinhos datados. */
  const dateErrors = useMemo(() => {
    const errors: Record<string, string | null> = {};
    drafts.forEach((draft, index) => {
      const previousSiblingDueDate = [...drafts.slice(0, index)]
        .reverse()
        .find((d) => d.due_date)?.due_date;
      const nextSiblingDueDate = drafts
        .slice(index + 1)
        .find((d) => d.due_date)?.due_date;
      errors[draft.tempId] = getSubtaskDateError({
        parentDueDate,
        subtaskDueDate: draft.due_date,
        previousSiblingDueDate,
        nextSiblingDueDate,
      });
    });
    return errors;
  }, [drafts, parentDueDate]);

  const handleAdd = () => {
    const title = newTitle.trim();
    if (!title) return;
    const tempId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    const draft: DraftSubtask = {
      tempId,
      title,
      description: "",
      assignee_member_ids: [memberId],
      due_date: null,
      due_time: null,
    };
    onChange([...drafts, draft]);
    setLastAddedId(tempId);
    setNewTitle("");
  };

  const handlePatch = (tempId: string, patch: Partial<DraftSubtask>) => {
    onChange(drafts.map((d) => (d.tempId === tempId ? { ...d, ...patch } : d)));
  };

  const handleRemove = (tempId: string) => {
    onChange(drafts.filter((d) => d.tempId !== tempId));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">
          Subtarefas{drafts.length > 0 ? ` (${drafts.length})` : ""}
        </h3>
      </div>

      {drafts.length > 0 && (
        <div className="space-y-2">
          {drafts.map((d) => (
            <DraftSubtaskRow
              key={d.tempId}
              draft={d}
              onChange={(patch) => handlePatch(d.tempId, patch)}
              onRemove={() => handleRemove(d.tempId)}
              isAdmin={isAdmin}
              members={members}
              memberName={memberName}
              minDate={minDate}
              dateError={dateErrors[d.tempId]}
              defaultOpen={d.tempId === lastAddedId}
            />
          ))}
        </div>
      )}

      <SubtaskComposerRow
        title={newTitle}
        onTitleChange={setNewTitle}
        onAdd={handleAdd}
      />
    </div>
  );
}
